import { useMemo } from 'react';
import { Network } from 'lucide-react';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, EmptyState } from './components/hr-ui';
import { useEmployees } from './hr-store';
import { EMPLOYMENT_STATUS_BADGE, type Employee } from '@/client/types/hr';
import { cn } from '@/lib/utils';

type TreeNode = { employee: Employee; children: TreeNode[] };

function buildTree(employees: Employee[]): TreeNode[] {
  const ids = new Set(employees.map((e) => e.id));
  const byManager = new Map<string | undefined, Employee[]>();
  for (const e of employees) {
    // No manager, or a manager not in the list, makes a root.
    const key = e.managerId && ids.has(e.managerId) ? e.managerId : undefined;
    byManager.set(key, [...(byManager.get(key) ?? []), e]);
  }
  const build = (managerId?: string): TreeNode[] =>
    (byManager.get(managerId) ?? []).map((employee) => ({ employee, children: build(employee.id) }));
  return build(undefined);
}

function OrgNode({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  const { employee, children } = node;
  return (
    <div className={cn(depth > 0 && 'ml-6 border-l border-gray-200 pl-6')}>
      <div className="mb-2 flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm">
        <EmployeeAvatar employee={employee} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{employee.name}</p>
          <p className="truncate text-xs text-gray-400">{employee.title}</p>
        </div>
        <span className={cn('ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium', EMPLOYMENT_STATUS_BADGE[employee.status])}>
          {employee.department}
        </span>
      </div>
      {children.length > 0 && (
        <div className="space-y-2 pb-2">
          {children.map((child) => (
            <OrgNode key={child.employee.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

const OrgChartPage = () => {
  const employees = useEmployees();
  const tree = useMemo(() => buildTree(employees), [employees]);

  return (
    <HrPageShell
      mock={false}
      title="Org chart"
      description="Reporting lines across the company, derived from each employee's manager."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'People' }, { label: 'Org chart' }]}
      icon={Network}
    >
      {tree.length === 0 ? (
        <EmptyState label="No employees to chart yet." />
      ) : (
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
          <div className="space-y-3">
            {tree.map((root) => (
              <OrgNode key={root.employee.id} node={root} />
            ))}
          </div>
        </div>
      )}
    </HrPageShell>
  );
};

export default OrgChartPage;
